import { Link } from 'react-router-dom';
import { Shield, FileText, HelpCircle } from 'lucide-react';

// App data — add new apps here
const APPS = [
  {
    id: 'clippilot',
    name: 'ClipPilot',
    tagline: 'Smart, Secure Clipboard History',
    description: 'A privacy-first clipboard manager that automatically saves everything you copy. Features smart classification, Face ID/PIN protection, keyboard extension for quick access, and optional iCloud sync.',
    icon: '/app-icons/clippilot.png',
    color: '#d100a3',
  },
  {
    id: 'hydratedaily',
    name: 'Hydrate Daily',
    tagline: 'Simple Water Intake Tracker',
    description: 'A privacy-focused hydration tracker to help you stay healthy. Set daily goals, track your water intake with quick-add buttons, build streaks, view your history, and get customizable reminders. 100% offline with no data collection.',
    icon: '/app-icons/hydratedaily.png',
    color: '#0891b2',
  },
  {
    id: 'prismatica',
    name: 'Prismatica',
    tagline: 'Professional Camera Controls',
    description: 'A powerful camera app with professional manual controls for capturing stunning photos and videos. Features include tap-to-focus, pinch-to-zoom, customizable floating panels, real-time filters, performance monitoring, grid overlays, and support for multiple formats including HEIC, DNG, and ProRes.',
    icon: '/app-icons/prismatica.png',
    color: '#8b5cf6',
  },
  {
    id: 'tapledger',
    name: 'TapLedger',
    tagline: 'Fast, Lightweight Expense Tracking',
    description: 'A quick and simple expense tracker designed for speed. Log daily expenses with categories and payment types, view spending summaries, and analyze your habits with daily, weekly, and monthly breakdowns. 100% offline with no data collection.',
    icon: '/app-icons/tapledger.png',
    color: '#10b981',
  },
  {
    id: 'packpal',
    name: 'PackPal',
    tagline: 'Smart Packing Lists for Every Trip',
    description: 'An AI-powered packing list generator that takes the stress out of travel prep. Create smart packing lists based on your destination, activities, weather, and trip duration. Choose from 7 pre-built templates, organize items across 15 categories with priority levels, and generate day-by-day itineraries. All data stored locally on your device.',
    icon: '/app-icons/packpal.png',
    color: '#f59e0b',
  },
  {
    id: 'clearcast',
    name: 'Clearcast',
    tagline: 'Weather-Smart Activity Planner',
    description: 'A privacy-first activity planner that scores every hour of the week against your personal weather preferences so you know exactly when to run, cycle, play, or hike. Real-time WeatherKit forecasts, sport-specific insights for football and cricket, solar & golden-hour timing, and optional on-device learning from Apple Health. Pro unlocks unlimited activities, the 7-day planner, and smart window alerts.',
    icon: '/app-icons/clearcast.png',
    color: '#0d7377',
  },
  {
    id: 'ensemble',
    name: 'Ensemble',
    tagline: 'Multi-Dish Cooking, Perfectly Timed',
    description: 'An AI-powered cooking orchestrator that schedules every step of every recipe backwards from a single finish time, so every dish lands on the table together. Import recipes via search, URL, manual entry, or photo scan. Focused cooking mode with per-step timers, haptics, voice commands, and background notifications for long simmers and bakes. 100% offline for core features. One-time Ensemble Pro unlock adds Photo Scan, Voice Commands, and unlimited dishes per meal.',
    icon: '/app-icons/ensemble.png',
    color: '#b87333',
  },
  {
    id: 'bloc',
    name: 'Bloc',
    tagline: 'A Board for Your Building',
    description: 'A simple, organized noticeboard for your building. Post notices, alerts, marketplace listings, events, help requests, and giveaways across six clear categories, pin important notices, and post anonymously when a topic is sensitive. Everything is stored on your device.',
    icon: '/app-icons/bloc.png',
    color: '#3b82f6',
  },
  {
    id: 'bouncer',
    name: 'Bouncer',
    tagline: 'Track and Escalate Unpaid Invoices',
    description: 'A focused tracker for freelancers who are done chasing unpaid invoices from memory. Every invoice sits on a structured escalation timeline — from friendly reminder to final notice — with a dashboard of cash at risk. Escalation emails are drafted into your own mail app; nothing ever leaves your device.',
    icon: '/app-icons/bouncer.png',
    color: '#ef4444',
  },
  {
    id: 'claimly',
    name: 'Claimly',
    tagline: 'Return & Warranty Deadline Defense',
    description: 'Deadline defense for the money you spend. Log a purchase once and see how long you have to return it, how long its warranty lasts, and which free trials are about to charge you — with color-coded countdowns, built-in return policies for 20 major US retailers, and a Money at Risk dashboard. 100% on-device.',
    icon: '/app-icons/claimly.png',
    color: '#f59e0b',
  },
  {
    id: 'cue',
    name: 'Cue',
    tagline: 'Practice Difficult Conversations',
    description: 'A private space to rehearse difficult conversations with an AI partner — salary asks, tough feedback, hard talks. Practice by text or voice with on-device speech recognition, get coaching feedback, and build confidence session by session. Practice history stays on your device.',
    icon: '/app-icons/cue.png',
    color: '#8b5cf6',
  },
  {
    id: 'drift',
    name: 'Drift',
    tagline: 'Timed Breaks From Your Phone',
    description: 'Pick how long you want to be gone — 15 minutes to a half day — and Drift deals you a small real-world mission, then gets out of the way. Return to write a short reflection, tag your mood, and watch your offline streaks and calendar grow. Fully private, fully on-device.',
    icon: '/app-icons/drift.png',
    color: '#0ea5e9',
  },
  {
    id: 'gauge',
    name: 'Gauge',
    tagline: 'Home Project Cost Estimates',
    description: 'Rough out the cost of your next home project before you call a contractor. Describe the project, set dimensions and quality tier, and Gauge produces an itemized estimate from its built-in cost library — materials, labor, and totals — entirely on your device.',
    icon: '/app-icons/gauge.png',
    color: '#f97316',
  },
  {
    id: 'hush',
    name: 'Hush',
    tagline: 'Document Noise Disturbances',
    description: 'Keep an organized, factual record of noise disturbances at home. A live sound-level meter with severity bands, automatic incident detection above your threshold, one-tap manual logging, and a chronological timeline — exportable as a clean PDF report. Everything stays on your device.',
    icon: '/app-icons/hush.png',
    color: '#6366f1',
  },
  {
    id: 'hutch',
    name: 'Hutch',
    tagline: 'Turn Objects Into Playtime',
    description: 'Turn everyday household objects into hands-on activity ideas for kids. Scan with the camera or pick a photo — object detection runs on-device — or type objects in, and Hutch matches its offline activity library to what you have and your child’s age, with step-by-step instructions.',
    icon: '/app-icons/hutch.png',
    color: '#10b981',
  },
  {
    id: 'inflect',
    name: 'Inflect',
    tagline: 'See the Math Behind Big Moves',
    description: 'Big life decisions have math hiding inside them. Pick a decision — Rent vs. Buy, Job Switch, Have a Baby, Move Cities, Go Back to School, Start a Business — answer a short questionnaire with your own numbers, and get a year-by-year projection of how the two paths compare. Free, offline, no accounts.',
    icon: '/app-icons/inflect.png',
    color: '#14b8a6',
  },
  {
    id: 'lockin',
    name: 'LockIn',
    tagline: 'Focus Timer, Streaks & Stats',
    description: 'A distraction-free deep work timer for people who measure progress in focused hours. Pick Study, Coding, or Trading mode, start a 25–90 minute session or a custom length, build daily streaks, and review honest history — including the sessions you cut short.',
    icon: '/app-icons/lockin.png',
    color: '#eab308',
  },
  {
    id: 'lore',
    name: 'Lore',
    tagline: 'Remember Every Little Detail',
    description: 'A private vault for the small facts you keep having to look up: birthdays, paint colors, clothing sizes, appliance models. Organized into People, Home, Health, and Style, searchable across every field at once, and pinnable. Everything stays on your iPhone.',
    icon: '/app-icons/lore.png',
    color: '#a855f7',
  },
  {
    id: 'mend',
    name: 'Mend',
    tagline: 'Your Clothing Repair Companion',
    description: 'Give your clothes a second life. Mend walks you through common clothing repairs with clear guides, helps you track your repair projects from torn to fixed, and keeps a log of everything you have saved from the landfill. Fully private, fully on-device.',
    icon: '/app-icons/mend.png',
    color: '#ec4899',
  },
  {
    id: 'merit',
    name: 'Merit',
    tagline: 'Voice-First Career Journal',
    description: 'Record your wins before you forget them. Speak a quick note about what you shipped, fixed, or led — Merit transcribes it on-device — and build a searchable career journal you can actually use when review season or an interview arrives.',
    icon: '/app-icons/merit.png',
    color: '#d97706',
  },
  {
    id: 'nuzzle',
    name: 'Nuzzle',
    tagline: 'Pet Medication & Health Tracking',
    description: 'Keep your pets’ medication routine organized. A Today dashboard of every scheduled dose, per-pet profiles with medications as your vet prescribed them, refill warnings, symptom logging, weight trends, and a history you can show your vet. All on your device.',
    icon: '/app-icons/nuzzle.png',
    color: '#fb7185',
  },
  {
    id: 'proofly',
    name: 'Proofly',
    tagline: 'Scan Once. Prove Later.',
    description: 'A privacy-first receipt vault: capture receipt proof with your camera, let on-device text recognition pull out the details, organize receipts into bundles, and export clean reimbursement or tax packets when you need them. No accounts, no uploads.',
    icon: '/app-icons/proofly.png',
    color: '#22c55e',
  },
  {
    id: 'proviso',
    name: 'Proviso',
    tagline: 'Contract Red Flags, In Plain English',
    description: 'Turn dense contract text into a clause-by-clause breakdown you can actually read. Scan paper contracts, import PDFs, or paste text; every clause gets a category, a Standard / Review / Red Flag marker, and a plain-English note — plus an overall risk score. Analysis runs entirely on your device.',
    icon: '/app-icons/proviso.png',
    color: '#0d9488',
  },
  {
    id: 'quorum',
    name: 'Quorum',
    tagline: 'Lock In Plans With Friends',
    description: 'The decision-closure engine for friend groups. Create a plan with options and a decision deadline, collect the group’s picks, and lock in a winner — so 40-message group-chat spirals turn into one clear answer. Plans live on your device; share summaries through your own messaging apps.',
    icon: '/app-icons/quorum.png',
    color: '#7c3aed',
  },
  {
    id: 'resonance',
    name: 'Resonance',
    tagline: 'Turn Sound Into Music',
    description: 'Capture the sounds around you and turn them into music. Resonance records audio, analyzes it on your device, and helps you shape it into musical ideas — with playback tools and a built-in metronome. Recordings never leave your phone.',
    icon: '/app-icons/resonance.png',
    color: '#e11d48',
  },
  {
    id: 'ripe',
    name: 'Ripe',
    tagline: 'Know What’s Fresh, Rescue What’s Not',
    description: 'Stop throwing food away. Track what’s in your kitchen with freshness windows, see what needs eating first, and rescue items before they turn. A simple, private tracker that keeps everything on your device.',
    icon: '/app-icons/ripe.png',
    color: '#84cc16',
  },
  {
    id: 'stance',
    name: 'Stance',
    tagline: 'Practice Interviews Out Loud',
    description: 'A private space to rehearse job interviews out loud. Pick an interview type, answer five questions with your voice, and see live transcripts, filler-word counts, and speaking pace — then a scorecard, daily challenges, and trend charts to sharpen your next answer.',
    icon: '/app-icons/stance.png',
    color: '#2563eb',
  },
  {
    id: 'steady',
    name: 'Steady',
    tagline: 'Energy Pacing for Chronic Fatigue',
    description: 'Pace your energy before the crash, not after. Steady reads HRV, resting heart rate, steps, and sleep from Apple Health — read-only, analyzed on your device — and combines them with your own check-ins to help you see when you are overreaching. Private by design, and not a medical device.',
    icon: '/app-icons/steady.png',
    color: '#16a34a',
  },
  {
    id: 'stillway',
    name: 'Stillway',
    tagline: 'Noise-Aware Routes for Calmer Walks',
    description: 'Find the quietest, most pleasant walking route — not just the fastest. Stillway scores routes for calm using on-device noise estimation and green-space awareness, over routes fetched from Apple Maps. Your location is used on your device and never sent to us.',
    icon: '/app-icons/stillway.png',
    color: '#0891b2',
  },
  {
    id: 'synapse',
    name: 'Synapse',
    tagline: 'Smarter Routes for Mumbai',
    description: 'A route comparison navigator built for Mumbai. Compare route options with toll costs, traffic conditions, and road quality side by side, using Apple Maps — or TomTom or Google routing when configured. Your trips and preferences stay on your device.',
    icon: '/app-icons/synapse.png',
    color: '#dc2626',
  },
  {
    id: 'tether',
    name: 'Tether',
    tagline: 'Screen-Time Pacts, Kept Together',
    description: 'Willpower is a team sport. Create screen-time pacts — clear commitments about when your phone stays down — track how you hold up, and build the streak. Pacts and progress stay entirely on your device.',
    icon: '/app-icons/tether.png',
    color: '#9333ea',
  },
  {
    id: 'unearth',
    name: 'Unearth',
    tagline: 'Unearth Your City, One Mission at a Time',
    description: 'Turn your city into a discovery game. Generate exploration missions, log what you find with notes and optional photos, and build a personal atlas of places you have unearthed. Location is used on your device; discoveries stay yours.',
    icon: '/app-icons/unearth.png',
    color: '#b45309',
  },
  {
    id: 'upkeep',
    name: 'Upkeep',
    tagline: 'Your Life Runs Better Maintained',
    description: 'The maintenance tracker for everything you own: schedule recurring upkeep for your home, car, and gear, get reminded before things slip, and keep a photo log of what was done. All on your device.',
    icon: '/app-icons/upkeep.png',
    color: '#64748b',
  },
  {
    id: 'verdict',
    name: 'Verdict',
    tagline: 'Track Decisions, Learn Your Patterns',
    description: 'The anti-diary: log what you decided, why, and what you expect — then review it later against what actually happened. Confidence calibration, outcome patterns by category, and review reminders that turn hindsight into a skill. All on your device.',
    icon: '/app-icons/verdict.png',
    color: '#4f46e5',
  },
  {
    id: 'tomorrowboard',
    name: 'TomorrowBoard',
    tagline: 'School Chats, Turned Into a Board',
    description: 'Turn chaotic school group-chat screenshots into a clean "what matters tomorrow" board. Import a screenshot, on-device text recognition finds the action items — supplies, forms, fees, date changes — you confirm them, and they land on a board sorted into Tomorrow and This Week. Zero network requests.',
    icon: '/app-icons/tomorrowboard.png',
    color: '#db2777',
  },
  {
    id: 'perisignal',
    name: 'PeriSignal',
    tagline: 'A Clear Record for Perimenopause Visits',
    description: 'A private daily journal for perimenopause: rate eight curated symptoms in under 30 seconds, see 30-day patterns, and walk into appointments with a clinician-ready Visit Brief PDF. No accounts, no network, no interpretation — your words, organized.',
    icon: '/app-icons/perisignal.png',
    color: '#c026d3',
  },
  {
    id: 'afterdose',
    name: 'AfterDose',
    tagline: 'Know What Changed After the Dose Changed',
    description: 'A small, private journal for the weeks after a medication adjustment. Record the change once, check in for a minute a day with six quick ratings, and bring a clear before-and-after Visit Summary PDF to your prescriber. Fully offline; never suggests medications or doses.',
    icon: '/app-icons/afterdose.png',
    color: '#4338ca',
  },
  {
    id: 'handrail',
    name: 'Handrail',
    tagline: 'Start the Task You’ve Been Avoiding',
    description: 'A calm, adult-feeling app for ADHD brains that turns avoided tasks into one tiny physical first move. Shrink the task, run a 2-minute timer, save blueprints for the starts that work, and keep a shame-free log of every start. Fully offline.',
    icon: '/app-icons/handrail.png',
    color: '#059669',
  },
  {
    id: 'stowry',
    name: 'Stowry',
    tagline: 'Remember Where Your Stuff Lives',
    description: 'Stop the frantic search before leaving home. Stowry remembers where your important items live — passport, spare keys, chargers — with a home spot and last-put location for each, optional spot photos, and readiness checks before trips. Fully offline.',
    icon: '/app-icons/stowry.png',
    color: '#ca8a04',
  },
  {
    id: 'frontporch',
    name: 'FrontPorch',
    tagline: 'A Family Bulletin Board for Elders',
    description: 'A widget-first family bulletin board for older relatives: an auto-cycling board of photos, notes, and reminder cards, curated by you, glanceable from the home screen. Everything lives on the device — a calm, ambient way to stay present.',
    icon: '/app-icons/frontporch.png',
    color: '#ea580c',
  },
  {
    id: 'kinrelay',
    name: 'KinRelay',
    tagline: 'The Command Center for Sibling Caregiving',
    description: 'One place for who owns what, what changed, and what happens next when siblings share care of aging parents. A task board with clear ownership, a changelog of every update, key documents at hand, and Care Updates you can export as text or PDF. All on your device.',
    icon: '/app-icons/kinrelay.png',
    color: '#0369a1',
  },
];

const LEGAL = [
  { kind: 'privacy', label: 'Privacy Policy', icon: Shield },
  { kind: 'terms', label: 'Terms & Conditions', icon: FileText },
  { kind: 'support', label: 'Support', icon: HelpCircle },
];

const AppsPage = () => (
  <div className="r-root legal-page" style={{ paddingTop: '150px' }}>
    <div className="r-page-bg" aria-hidden="true" />
    <div className="r-wrap">
      <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto' }}>
        <div className="r-tag" style={{ justifyContent: 'center' }}>Our apps</div>
        <h1 className="r-head">
          Native iOS apps, <span className="r-gtxt">crafted with care.</span>
        </h1>
        <p className="r-sub" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
          A growing suite of privacy-first apps across health, finance, productivity, and
          creativity. Explore each one and its legal documentation below.
        </p>
      </div>

      <div className="apps-grid">
        {APPS.map((app) => (
          <article key={app.id} className="app-card">
            <img src={app.icon} alt={app.name} className="app-icon" />
            <div style={{ flex: 1 }}>
              <h2 className="app-name">{app.name}</h2>
              <p className="app-tagline" style={{ color: app.color }}>{app.tagline}</p>
              <p className="app-desc">{app.description}</p>
              <div className="app-links">
                {LEGAL.map(({ kind, label, icon: Icon }) => (
                  <Link key={kind} to={`/apps/${app.id}/${kind}`} className="app-link">
                    <Icon className="w-4 h-4" />
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>

      <p style={{ textAlign: 'center', marginTop: '40px', fontFamily: 'var(--ft-font-mono)', fontSize: '12px', color: 'var(--text-muted)' }}>
        More apps coming soon. Stay tuned.
      </p>
    </div>
  </div>
);

export default AppsPage;
